/* eslint-disable no-console */
/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */

'use client';

import React, {
  forwardRef,
  type JSX,
  memo,
  type UIEvent,
  useCallback,
  useRef,
} from 'react';
import type {
  DragPreviewRenderer,
  DroppableCollectionResult,
} from 'react-aria';
import { useFocusRing, useGridList } from 'react-aria';
import type {
  DraggableCollectionState,
  DroppableCollectionState,
} from 'react-stately';
import { Portal } from 'components/Portal';
import { styledScrollbarMixin } from 'components/ScrollContainer';
import {
  getShowingScrollbars,
  type ScrollBoxProps,
} from 'components/ScrollContainer/ScrollContainer';
import styled from 'styled-components';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import { AnimateItemGroup, AnimateMutationProvider } from '../animations';
import styles from '../Collections.module.css';
import { DragAndDropProvider } from '../dnd/DragAndDropProvider';
import { useIsScrolling } from '../hooks/useIsScrolling';
import { ListBoxItem } from './components/ListBoxItem';
import { ListBoxLoading } from './components/ListBoxLoading';
import { ListBoxSection } from './components/ListBoxSection';
import { ignoreInputTargetElements } from './components/utils';
import type { ListBoxProps } from './types';
import { useVirtualizedList } from './useVirtualizedList';

function ListBox<T extends object>(
  { state, loading, onScroll, ...props }: ListBoxProps<T>,
  forwardedRef: React.Ref<HTMLDivElement>
) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const gridListRef = useRef(null);

  const {
    listProps: {
      visibleRect,
      isFullyMeasured,
      layoutInfo,
      totalSize,
      nodes,
      updateItemSize,
      keyboardDelegate,
      dropTargetDelegate,
    },
  } = useVirtualizedList(props, state, scrollRef);

  const { gridProps } = useGridList(
    {
      ...props,
      keyboardDelegate,
      isVirtualized: true,
    },
    state,
    gridListRef
  );

  /**
   * Scrolling behaviour support
   */
  const isScrolling = useIsScrolling(scrollRef);
  // Add scroll handler
  const handleScroll = useCallback(
    (evt: UIEvent<HTMLDivElement>) => {
      if (!onScroll) return;
      const { scrollTop, scrollLeft } = evt.currentTarget;
      onScroll(evt, {
        scrollTop,
        scrollLeft,
      });
    },
    [onScroll]
  );

  const { focusProps, isFocusVisible } = useFocusRing();

  /**
   * Draggable support
   */

  const { dragAndDropHooks } = props;

  let dragState: DraggableCollectionState | undefined;
  let dragPreview: JSX.Element | null = null;
  const preview = useRef<DragPreviewRenderer>(null);

  const isListDraggable = !!dragAndDropHooks?.useDraggableCollectionState;

  if (isListDraggable && dragAndDropHooks) {
    dragState = dragAndDropHooks.useDraggableCollectionState!({
      collection: state.collection,
      selectionManager: state.selectionManager,
      preview: dragAndDropHooks.renderDragPreview ? preview : undefined,
    });

    dragAndDropHooks.useDraggableCollection!({}, dragState, gridListRef);

    const DragPreview = dragAndDropHooks.DragPreview!;

    dragPreview = dragAndDropHooks.renderDragPreview ? (
      <Portal>
        <DragPreview ref={preview}>
          {dragAndDropHooks.renderDragPreview}
        </DragPreview>
      </Portal>
    ) : null;
  }

  /**
   * Droppable support
   */

  let dropState: DroppableCollectionState | undefined;
  let droppableCollection: DroppableCollectionResult | undefined;
  let isRootDropTarget = false;

  const isListDroppable = !!dragAndDropHooks?.useDroppableCollectionState;

  if (isListDroppable && dragAndDropHooks) {
    dropState = dragAndDropHooks.useDroppableCollectionState!({
      collection: state.collection,
      selectionManager: state.selectionManager,
    });

    droppableCollection = dragAndDropHooks.useDroppableCollection!(
      {
        keyboardDelegate,
        dropTargetDelegate,
      },
      dropState,
      gridListRef
    );

    isRootDropTarget = dropState.isDropTarget({ type: 'root' });
  }

  return (
    <DragAndDropProvider
      dragAndDropHooks={dragAndDropHooks}
      dragState={dragState}
      dropState={dropState}
    >
      <StyledScrollView
        ref={scrollRef}
        $shouldUseCustomStyles={Boolean(getShowingScrollbars())}
        className={styles.scrollView}
        data-focus-visible={isFocusVisible || undefined}
        data-loading={loading || undefined}
        data-root-drop-target={isRootDropTarget || undefined}
        onScroll={handleScroll}
        {...droppableCollection?.collectionProps}
      >
        <AnimateMutationProvider
          duration={350}
          enabled={props.animate && !state.queryState.focused && !isScrolling}
          isFullyMeasured={isFullyMeasured}
          layoutInfo={layoutInfo}
          state={state}
          visibleRect={visibleRect}
        >
          <div
            ref={mergeRefs(forwardedRef, gridListRef)}
            className={styles.listBox}
            {...mergeProps(
              {
                ...gridProps,
                // `useGridList` prevents default `onKeyDownCapture` to handle
                // type to focus behavior. We need react-aria to only handle
                // this if the keydown event does not occur within a text input.
                onKeyDownCapture: ignoreInputTargetElements(
                  // @ts-expect-error FIXME(strictNullChecks)
                  gridProps.onKeyDownCapture
                ),
              },
              focusProps
            )}
            style={
              {
                height: totalSize,
                pointerEvents: isScrolling ? 'none' : 'inherit',
              } as React.CSSProperties
            }
          >
            <AnimateItemGroup>
              {nodes.map((node, i) => {
                if (node.type === 'section') {
                  const item = state.collection.getItem(node.key);

                  if (item) {
                    return (
                      <ListBoxSection
                        key={node.key}
                        className={item.props?.className}
                        id={node.key}
                        isHeaderSticky={item.props?.isHeaderSticky}
                        isHeaderVisible={item.props?.isHeaderVisible}
                        node={node}
                        sectionGap={props.sectionGap ?? 0}
                        state={state}
                        updateItemSize={updateItemSize}
                        y={node.rect.y}
                      />
                    );
                  }
                  return null;
                }

                if (node.type === 'item') {
                  const prevChild = nodes[i - 1];
                  const item = state.collection.getItem(node.key);

                  if (item) {
                    return (
                      <ListBoxItem
                        key={node.key}
                        className={item.props?.className}
                        gapAfter={node.gapEnd}
                        gapBefore={(prevChild as any)?.gapEnd}
                        height={node.rect.height}
                        id={node.key}
                        state={state}
                        updateItemSize={updateItemSize}
                        variant={item.props?.variant}
                        y={node.rect.y}
                      >
                        {item.rendered}
                      </ListBoxItem>
                    );
                  }
                  return null;
                }
              })}
            </AnimateItemGroup>

            {dragPreview}
          </div>
        </AnimateMutationProvider>
        <ListBoxLoading
          collectionSize={state.collection.size}
          loading={loading}
          query={state.queryState.query}
          renderLoading={props.renderLoading}
          renderPlaceholder={props.renderPlaceholder}
        />
      </StyledScrollView>
    </DragAndDropProvider>
  );
}

// Keep styled-component for scrollbar mixin
const StyledScrollView = styled.div<ScrollBoxProps>`
  ${styledScrollbarMixin};
`;

const _ListBox = memo(forwardRef(ListBox)) as <T extends object>(
  props: ListBoxProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => JSX.Element;

export { _ListBox as ListBox };
